import { Menu, MenuList, MenuButton, MenuItem } from '@reach/menu-button';
import { Tooltip } from '@reach/tooltip';
import cn from 'clsx';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React from 'react';
import { Icon } from '@/components/atom';
import styles from './search.module.less';

/**
 * Wait time for debounce function for setting search query into global state.
 * Generally we want to wait till user finishes typing query and then set it globally
 * for filtering. There's no point doing this on every new key typed.
 *
 * @type {number} Wait time in ms.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DEBOUNCE_SET_QUERY = 250;
export default class Search extends React.PureComponent {
  static get propTypes() {
    return {
      entriesLength: PropTypes.number,
      showingLength: PropTypes.number,
      searchQuery: PropTypes.string,
      hasNext: PropTypes.bool,
      hasPrevious: PropTypes.bool,
      clearEntries: PropTypes.func,
      setSearchQuery: PropTypes.func,
      selectNext: PropTypes.func,
      selectPrevious: PropTypes.func
    };
  }
  constructor(props) {
    super(props);
    this._setQuery = debounce(() => {
      const query = this._validateQuery(this.state.query);
      if (query !== null) {
        this.props.setSearchQuery(query);
        this.setState({
          invalid: false
        });
      } else {
        this.setState({
          invalid: true
        });
      }
    }, DEBOUNCE_SET_QUERY);
    this.state = {
      query: props.searchQuery,
      invalid: false
    };
  }
  render() {
    return /*#__PURE__*/_jsxs("div", {
      className: styles.toolbar,
      children: [this._renderSearchInput(), /*#__PURE__*/_jsx("span", {
        className: styles.separator
      }), this._renderActionButtons(), /*#__PURE__*/_jsx("span", {
        className: styles.separator
      }), this._renderClearButton(), /*#__PURE__*/_jsx("span", {
        className: styles.separator
      }), this._renderMenu()]
    });
  }
  _renderSearchInput() {
    const {
      showingLength,
      entriesLength
    } = this.props;
    const {
      query,
      invalid
    } = this.state;
    return /*#__PURE__*/_jsxs("div", {
      className: styles.search,
      children: [/*#__PURE__*/_jsx("span", {
        className: cn(styles['icon'], styles['icon--large'], styles['icon--disabled']),
        children: /*#__PURE__*/_jsx(Icon, {
          name: "search"
        })
      }), /*#__PURE__*/_jsx("input", {
        type: "text",
        value: query,
        onChange: e => this.onChange(e),
        className: cn(styles.searchInput, {
          [styles['searchInput--invalid']]: invalid
        }),
        placeholder: "Search (text or /regex/)"
      }), /*#__PURE__*/_jsxs("span", {
        className: styles.showing,
        children: ["showing\xA0", showingLength, "/", entriesLength, "\xA0items"]
      })]
    });
  }
  _renderActionButtons() {
    const {
      hasNext,
      hasPrevious,
      selectNext,
      selectPrevious
    } = this.props;
    const {
      query
    } = this.state;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        label: "Select previous item",
        children: /*#__PURE__*/_jsx("button", {
          disabled: !hasPrevious,
          onClick: selectPrevious,
          className: cn(styles.btn, styles.icon),
          children: /*#__PURE__*/_jsx(Icon, {
            name: "arrowUp"
          })
        })
      }), /*#__PURE__*/_jsx(Tooltip, {
        label: "Select next item",
        children: /*#__PURE__*/_jsx("button", {
          disabled: !hasNext,
          onClick: selectNext,
          className: cn(styles.btn, styles.icon),
          children: /*#__PURE__*/_jsx(Icon, {
            name: "arrowDown"
          })
        })
      }), /*#__PURE__*/_jsx(Tooltip, {
        label: "Clear search",
        children: /*#__PURE__*/_jsx("button", {
          disabled: query.length <= 0,
          onClick: e => this.onClear(e),
          className: cn(styles.btn, styles.icon),
          children: /*#__PURE__*/_jsx(Icon, {
            name: "close"
          })
        })
      })]
    });
  }
  _renderClearButton() {
    const {
      clearEntries
    } = this.props;
    return /*#__PURE__*/_jsx(Tooltip, {
      label: "Clear entries",
      children: /*#__PURE__*/_jsx("button", {
        onClick: clearEntries,
        className: cn(styles.btn, styles.icon),
        children: /*#__PURE__*/_jsx(Icon, {
          name: "reset"
        })
      })
    });
  }
  _renderMenu() {
    return /*#__PURE__*/_jsxs(Menu, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        label: "More options",
        children: /*#__PURE__*/_jsx(MenuButton, {
          className: cn(styles.btn, styles.icon),
          children: /*#__PURE__*/_jsx(Icon, {
            name: "more"
          })
        })
      }), /*#__PURE__*/_jsx(MenuList, {
        children: /*#__PURE__*/_jsxs(MenuItem, {
          onSelect: () => chrome.runtime.openOptionsPage(),
          children: [/*#__PURE__*/_jsx("span", {
            className: "menu-item__label",
            children: "Settings"
          }), /*#__PURE__*/_jsx(Icon, {
            name: "cog"
          })]
        })
      })]
    });
  }
  onChange({
    target: {
      value
    }
  }) {
    this.setState({
      query: value
    }, this._setQuery);
  }
  onClear(e) {
    e.preventDefault();
    this.props.setSearchQuery('');
    this.setState({
      query: ''
    });
  }
  _validateQuery(query) {
    try {
      if (query[0] !== '/') {
        return query;
      } else {
        // Ignore search if regexp is not yet complete or invalid
        if (!/\/[\s\S]+\/[\s\S]*/.test(query)) {
          return null;
        }

        // Test if regexp is correct
        // eslint-disable-next-line no-constant-condition
        if (new RegExp(query)) {
          return query;
        }
      }
    } catch (e) {
      return null;
    }
  }
}